import {
  Dispatch,
  FC,
  Fragment,
  SetStateAction,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronRightIcon, DocumentTextIcon } from "@heroicons/react/outline";
import { FolderIcon } from "@heroicons/react/solid";
import { Base64toObject, formatFileSize } from "helpers/utils";
import { FileQuote, QuoteFileDirectory } from "types/File";

export type TreeNode = {
  uid: string;
  kind: "folder" | "file";
  id: number;
  name: string;
  parentId: number | null;
  raw: FileQuote | QuoteFileDirectory;
  children: TreeNode[];
  // A file's own size, a folder's whole subtree. Files the API has no size for
  // are counted in `unknownFiles` instead of as zero, so a folder total can say
  // it is a lower bound rather than quietly under-reporting.
  sizeBytes: number;
  unknownFiles: number;
};

export type FilesSize = { bytes: number; unknownFiles: number };

/** Bytes of a set of files, plus how many of them have no size stored. */
export const sumFileSizes = (files: FileQuote[]): FilesSize =>
  files.reduce<FilesSize>(
    (acc, file) => {
      if (typeof file.fileSize === "number" && file.fileSize >= 0) {
        acc.bytes += file.fileSize;
      } else {
        acc.unknownFiles += 1;
      }
      return acc;
    },
    { bytes: 0, unknownFiles: 0 },
  );

/** `~` marks a total that some file is missing from. */
export const formatSizeTotal = ({ bytes, unknownFiles }: FilesSize): string => {
  // Nothing measurable at all – say unknown rather than print a zero, which
  // would read as "empty". Files stored before sizes were recorded land here.
  if (!bytes && unknownFiles) return "—";

  return `${unknownFiles ? "~" : ""}${formatFileSize(bytes)}`;
};

export const sizeTotalTitle = ({ bytes, unknownFiles }: FilesSize): string => {
  if (!unknownFiles) return formatFileSize(bytes);

  const missing = `${unknownFiles} file${unknownFiles > 1 ? "s" : ""}`;

  return bytes
    ? `At least ${formatFileSize(bytes)} – the size of ${missing} was never recorded`
    : `Unknown: the size of ${missing} was never recorded`;
};

const fileHref = (file: FileQuote): string | undefined =>
  file.fileURL || file.fileUid || Base64toObject(file.file) || undefined;

export const folderUid = (id: number): string => `folder-${id}`;

// The root folder of a "Data Uploads" quote line. The server names it from that
// line, so it is not renamed, deleted or moved into another folder by hand.
const isSystemFolder = (node: TreeNode): boolean =>
  node.kind === "folder" &&
  (node.raw as QuoteFileDirectory).orderProductId != null;

/**
 * Uids of every folder this widget draws – the card header needs them to expand
 * the whole tree in one click, and it only owns the flat `directories` list.
 */
export const getFolderUids = (
  directories: QuoteFileDirectory[],
  availableForSupplier: boolean,
): string[] =>
  directories
    .filter((dir) => !!dir.availableForSupplier === availableForSupplier)
    .map((dir) => folderUid(dir.id));

const AUTO_EXPAND_DELAY = 700;

const QuoteFilesTree: FC<{
  files: FileQuote[];
  directories: QuoteFileDirectory[];
  availableForSupplier: boolean;
  canManage: boolean;
  // Separate from canManage on purpose: readonlyprocesing and logoaccount can manage folders
  // but hold no uploadFile button, and the upload mutations answer them with a bare 403.
  canUpload?: boolean;
  // Picks this folder as the target of the card's upload form.
  onUploadHere?: (dirId: number, name: string) => void;
  currentFileId?: number;
  editingComment: string;
  onEditingCommentChange: (value: string) => void;
  onSaveComment: () => void;
  onCancelEdit: () => void;
  isSavingComment: boolean;
  onSelectFile: (id: number) => void;
  onRequestDeleteFile: (id: number) => void;
  onRequestDeleteDirectory: (id: number) => void;
  onMoveFile: (fileId: number, directoryId: number | null) => void;
  onMoveDirectory: (dirId: number, parentId: number | null) => void;
  // Opens the add-folder input in the card header, targeting this parent.
  onAddDirectory: (parentId: number | null, parentName?: string) => void;
  onRenameDirectory: (id: number, currentName: string) => void;
  // Download a root folder's subtree as a ZIP.
  onDownloadDirectory: (dirId: number) => void;
  downloadingZipKey: string | null;
  // Which folders are open. Owned by the card so its header buttons can expand
  // or collapse the whole tree; a uid that is absent is collapsed, so folders
  // start closed and newly created ones stay closed.
  expandedUids: Set<string>;
  setExpandedUids: Dispatch<SetStateAction<Set<string>>>;
}> = ({
  files,
  directories,
  availableForSupplier,
  canManage,
  canUpload = false,
  onUploadHere,
  currentFileId,
  editingComment,
  onEditingCommentChange,
  onSaveComment,
  onCancelEdit,
  isSavingComment,
  onSelectFile,
  onRequestDeleteFile,
  onRequestDeleteDirectory,
  onMoveFile,
  onMoveDirectory,
  onAddDirectory,
  onRenameDirectory,
  onDownloadDirectory,
  downloadingZipKey,
  expandedUids,
  setExpandedUids,
}) => {
  const { treeItems, folderById, hasSystemDescendant } = useMemo(() => {
    const folderNodes = new Map<number, TreeNode>();
    directories
      .filter((dir) => !!dir.availableForSupplier === availableForSupplier)
      .forEach((dir) =>
        folderNodes.set(dir.id, {
          uid: folderUid(dir.id),
          kind: "folder",
          id: dir.id,
          name: dir.name,
          parentId: dir.parentId ?? null,
          raw: dir,
          children: [],
          sizeBytes: 0,
          unknownFiles: 0,
        }),
      );

    const roots: TreeNode[] = [];

    // Attach folders to their parent folder (or root when the parent is not
    // part of this widget's set).
    folderNodes.forEach((node) => {
      if (node.parentId != null && folderNodes.has(node.parentId)) {
        folderNodes.get(node.parentId)!.children.push(node);
      } else {
        roots.push(node);
      }
    });

    // Attach files under their directory (or root when directory is null or
    // belongs to the other widget).
    files.forEach((file) => {
      const size = sumFileSizes([file]);
      const node: TreeNode = {
        uid: `file-${file.id}`,
        kind: "file",
        id: file.id,
        name: file.fileName || "-",
        parentId: file.directoryId ?? null,
        raw: file,
        children: [],
        sizeBytes: size.bytes,
        unknownFiles: size.unknownFiles,
      };
      if (file.directoryId != null && folderNodes.has(file.directoryId)) {
        folderNodes.get(file.directoryId)!.children.push(node);
      } else {
        roots.push(node);
      }
    });

    // Folder totals roll up from the leaves, so a collapsed folder still shows
    // everything under it and not just the files sitting directly inside.
    const rollUpSizes = (node: TreeNode): void => {
      if (node.kind === "file") return;
      node.children.forEach(rollUpSizes);
      node.sizeBytes = node.children.reduce(
        (sum, child) => sum + child.sizeBytes,
        0,
      );
      node.unknownFiles = node.children.reduce(
        (sum, child) => sum + child.unknownFiles,
        0,
      );
    };
    roots.forEach(rollUpSizes);

    // Folders with a Data Uploads service folder somewhere below them: deleting
    // one takes its subtree, and the server refuses a subtree that holds a
    // service folder. Walked over every directory, guarding against parent_id
    // cycles.
    const directoryById = new Map(directories.map((dir) => [dir.id, dir]));
    const hasSystemDescendant = new Set<number>();
    directories.forEach((dir) => {
      if (dir.orderProductId == null) return;
      const seen = new Set<number>([dir.id]);
      let parentId = dir.parentId ?? null;
      while (parentId != null && !seen.has(parentId)) {
        seen.add(parentId);
        hasSystemDescendant.add(parentId);
        parentId = directoryById.get(parentId)?.parentId ?? null;
      }
    });

    return { treeItems: roots, folderById: folderNodes, hasSystemDescendant };
  }, [files, directories, availableForSupplier]);

  // "root" or a folder uid that is currently hovered as a drop target.
  const [dropTargetUid, setDropTargetUid] = useState<string | null>(null);
  const [dragUid, setDragUid] = useState<string | null>(null);

  const dragNodeRef = useRef<TreeNode | null>(null);
  const expandTimerRef = useRef<{ uid: string; id: number } | null>(null);

  const clearExpandTimer = () => {
    if (expandTimerRef.current) {
      window.clearTimeout(expandTimerRef.current.id);
      expandTimerRef.current = null;
    }
  };

  const cleanupDrag = () => {
    dragNodeRef.current = null;
    setDragUid(null);
    setDropTargetUid(null);
    clearExpandTimer();
  };

  const toggleExpanded = (uid: string) => {
    setExpandedUids((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) {
        next.delete(uid);
      } else {
        next.add(uid);
      }
      return next;
    });
  };

  // A folder cannot be dropped into itself or one of its own descendants.
  const isValidTarget = (
    drag: TreeNode,
    targetFolderId: number | null,
  ): boolean => {
    if (drag.kind !== "folder") return true;
    // A service folder only ever goes back to the top level (one dragged under
    // a staff folder before it was locked), never into another folder.
    if (isSystemFolder(drag)) return targetFolderId === null;
    let currentId = targetFolderId;
    while (currentId != null) {
      if (currentId === drag.id) return false;
      currentId = folderById.get(currentId)?.parentId ?? null;
    }
    return true;
  };

  const performDrop = (targetFolderId: number | null) => {
    const drag = dragNodeRef.current;
    cleanupDrag();
    if (!drag) return;
    if (!isValidTarget(drag, targetFolderId)) return;
    if (targetFolderId === drag.parentId) return; // nothing changed

    // Open the folder the item lands in, otherwise it just vanishes from view.
    if (targetFolderId != null) {
      setExpandedUids((prev) => {
        const next = new Set(prev);
        next.add(folderUid(targetFolderId));
        return next;
      });
    }

    if (drag.kind === "file") {
      onMoveFile(drag.id, targetFolderId);
    } else {
      onMoveDirectory(drag.id, targetFolderId);
    }
  };

  const onRowDragStart = (e: React.DragEvent, node: TreeNode) => {
    // Dragging selected text inside a row that is not draggable still fires
    // dragstart here – it must not start a move.
    if (!(e.currentTarget as HTMLElement).draggable) return;
    e.stopPropagation();
    dragNodeRef.current = node;
    setDragUid(node.uid);
    e.dataTransfer.effectAllowed = "move";
    // Firefox needs data for the drag to start.
    e.dataTransfer.setData("text/plain", node.uid);
  };

  const onFolderDragOver = (e: React.DragEvent, node: TreeNode) => {
    const drag = dragNodeRef.current;
    if (!drag) return; // drag from outside (other widget / OS files) – ignore
    e.stopPropagation();
    if (expandTimerRef.current && expandTimerRef.current.uid !== node.uid) {
      clearExpandTimer();
    }
    if (drag.uid === node.uid || !isValidTarget(drag, node.id)) {
      setDropTargetUid(null);
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTargetUid(node.uid);

    // Auto-expand a collapsed folder while hovering over it.
    if (
      node.children.length &&
      !expandedUids.has(node.uid) &&
      expandTimerRef.current?.uid !== node.uid
    ) {
      expandTimerRef.current = {
        uid: node.uid,
        id: window.setTimeout(() => {
          setExpandedUids((prev) => {
            const next = new Set(prev);
            next.add(node.uid);
            return next;
          });
          expandTimerRef.current = null;
        }, AUTO_EXPAND_DELAY),
      };
    }
  };

  // Dropping onto a file moves the dragged item into the file's folder.
  const onFileDragOver = (e: React.DragEvent, node: TreeNode) => {
    const drag = dragNodeRef.current;
    if (!drag) return;
    e.stopPropagation();
    clearExpandTimer();
    if (drag.uid === node.uid || !isValidTarget(drag, node.parentId)) {
      setDropTargetUid(null);
      return;
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTargetUid(
      node.parentId == null ? "root" : folderUid(node.parentId),
    );
  };

  const renderNode = (node: TreeNode, depth: number): JSX.Element => {
    const indentStyle = { paddingLeft: 6 + depth * 22 };

    if (node.kind === "folder") {
      const hasChildren = node.children.length > 0;
      const isExpanded = expandedUids.has(node.uid);
      const nodeSize: FilesSize = {
        bytes: node.sizeBytes,
        unknownFiles: node.unknownFiles,
      };
      const isSystem = isSystemFolder(node);
      const canDelete = !isSystem && !hasSystemDescendant.has(node.id);

      return (
        <Fragment key={node.uid}>
          <div
            className={`edit-quote__tree-row edit-quote__tree-row--folder ${
              hasChildren ? "cursor-pointer" : ""
            } ${dragUid === node.uid ? "edit-quote__tree-row--dragging" : ""} ${
              dropTargetUid === node.uid
                ? "edit-quote__tree-row--drop-target"
                : ""
            }`}
            style={indentStyle}
            // A service folder moves only when it has to go back to the top
            draggable={canManage && (!isSystem || node.parentId != null)}
            onDragStart={(e) => onRowDragStart(e, node)}
            onDragEnd={cleanupDrag}
            onDragOver={(e) => onFolderDragOver(e, node)}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              performDrop(node.id);
            }}
            onClick={hasChildren ? () => toggleExpanded(node.uid) : undefined}
          >
            <div className="edit-quote__tree-label">
              {hasChildren ? (
                <ChevronRightIcon
                  className={`edit-quote__tree-chevron ${
                    isExpanded ? "edit-quote__tree-chevron--open" : ""
                  }`}
                />
              ) : (
                <span className="edit-quote__tree-chevron" />
              )}
              <FolderIcon className="edit-quote__tree-icon text-[#ffc83d]" />
              <span
                className="edit-quote__tree-name"
                title={
                  isSystem
                    ? `${node.name}\nNamed automatically from the Data Uploads line and its product`
                    : node.name
                }
              >
                {node.name}
              </span>
            </div>
            <span
              className="edit-quote__tree-size"
              title={sizeTotalTitle(nodeSize)}
            >
              {formatSizeTotal(nodeSize)}
            </span>
            {/* Folder-row actions – revealed on hover: ZIP download (any
                level, all users), upload target when the role may upload,
                plus management actions when allowed. */}
            <div className="actions">
              <button
                className="edit-quote__tree-action"
                title="Download this folder as a ZIP archive"
                disabled={downloadingZipKey === `dir-${node.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onDownloadDirectory(node.id);
                }}
              >
                <i
                  className={
                    downloadingZipKey === `dir-${node.id}`
                      ? "inline-block animate-spin icon-spin4"
                      : "icon-export"
                  }
                />
              </button>
              {canUpload && !!onUploadHere && (
                <button
                  className="edit-quote__tree-action"
                  title="Upload into this folder"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUploadHere(node.id, node.name);
                  }}
                >
                  <i className="icon-upload" />
                </button>
              )}
              {canManage && (
                <Fragment>
                  <button
                    className="edit-quote__tree-action"
                    title="Add subfolder"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddDirectory(node.id, node.name);
                    }}
                  >
                    <i className="icon-plus" />
                  </button>
                  {/* Locked actions keep an invisible stand-in, so the size
                      column stays in line with the other folders. */}
                  {isSystem ? (
                    <span
                      className="edit-quote__tree-action invisible"
                      aria-hidden="true"
                    >
                      <i className="icon-pencil" />
                    </span>
                  ) : (
                    <button
                      className="edit-quote__tree-action"
                      title="Rename folder"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRenameDirectory(node.id, node.name);
                      }}
                    >
                      <i className="icon-pencil" />
                    </button>
                  )}
                  {canDelete ? (
                    <button
                      className="edit-quote__tree-action edit-quote__tree-action--danger"
                      title="Delete folder (and everything inside)"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRequestDeleteDirectory(node.id);
                      }}
                    >
                      <i className="icon-trash-empty" />
                    </button>
                  ) : (
                    <span
                      className="edit-quote__tree-action invisible"
                      aria-hidden="true"
                    >
                      <i className="icon-trash-empty" />
                    </span>
                  )}
                </Fragment>
              )}
            </div>
          </div>
          {hasChildren &&
            isExpanded &&
            node.children.map((child) => renderNode(child, depth + 1))}
        </Fragment>
      );
    }

    const file = node.raw as FileQuote;
    const href = fileHref(file);
    const metaParts = [file.user?.userName, file.comment].filter(Boolean);
    const isEditing = currentFileId === file.id;

    return (
      <div
        key={node.uid}
        className={`edit-quote__tree-row edit-quote__tree-row--file ${
          isEditing ? "edit-quote__tree-row--selected" : ""
        } ${dragUid === node.uid ? "edit-quote__tree-row--dragging" : ""}`}
        style={indentStyle}
        draggable={canManage && !isEditing}
        onDragStart={(e) => onRowDragStart(e, node)}
        onDragEnd={cleanupDrag}
        onDragOver={(e) => onFileDragOver(e, node)}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          performDrop(node.parentId);
        }}
      >
        <div className="edit-quote__tree-label">
          <span className="edit-quote__tree-chevron" />
          <DocumentTextIcon className="edit-quote__tree-icon text-gray-500" />
          {href ? (
            <a
              className="edit-quote__tree-name"
              href={href}
              target="_blank"
              rel="noreferrer"
              title={file.fileName}
              draggable={false}
            >
              {file.fileName || "-"}
            </a>
          ) : (
            <span className="edit-quote__tree-name" title={file.fileName}>
              {file.fileName || "-"}
            </span>
          )}
          {file.client && (
            <i
              className="icon-user text-green-500 flex-shrink-0"
              title={`${file.client.firstName || ""} ${
                file.client.lastName || ""
              } ${
                file.client.company ? `(${file.client.company})` : ""
              }`.trim()}
            />
          )}
          {isEditing && (
            <input
              className="edit-quote__tree-comment-input"
              value={editingComment}
              placeholder="Comment"
              autoFocus
              disabled={isSavingComment}
              onChange={(e) => onEditingCommentChange(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  onSaveComment();
                }
                if (e.key === "Escape") {
                  onCancelEdit();
                }
              }}
            />
          )}
          {!isEditing && !!metaParts.length && (
            <span
              className="edit-quote__tree-meta"
              title={metaParts.join(" · ")}
            >
              {metaParts.join(" · ")}
            </span>
          )}
        </div>
        <span
          className="edit-quote__tree-size"
          title={
            node.unknownFiles
              ? "The size of this file was not recorded"
              : undefined
          }
        >
          {formatFileSize(file.fileSize)}
        </span>
        {canManage && !isEditing && (
          <div className="actions">
            <button
              className="edit-quote__tree-action"
              title="Edit comment"
              onClick={(e) => {
                e.stopPropagation();
                onSelectFile(file.id);
              }}
            >
              <i className="icon-pencil" />
            </button>
            <button
              className="edit-quote__tree-action edit-quote__tree-action--danger"
              title="Delete file"
              onClick={(e) => {
                e.stopPropagation();
                onRequestDeleteFile(file.id);
              }}
            >
              <i className="icon-trash-empty" />
            </button>
          </div>
        )}
        {canManage && isEditing && (
          <div className="actions actions--visible">
            <button
              className="edit-quote__tree-action"
              title="Save comment"
              disabled={isSavingComment}
              onClick={(e) => {
                e.stopPropagation();
                onSaveComment();
              }}
            >
              <i
                className={
                  isSavingComment
                    ? "inline-block animate-spin icon-spin4"
                    : "icon-floppy"
                }
              />
            </button>
            <button
              className="edit-quote__tree-action edit-quote__tree-action--danger"
              title="Cancel"
              disabled={isSavingComment}
              onClick={(e) => {
                e.stopPropagation();
                onCancelEdit();
              }}
            >
              <i className="icon-cancel" />
            </button>
          </div>
        )}
      </div>
    );
  };

  if (!treeItems.length) {
    return (
      <div className="edit-quote__tree-empty text-gray-400 italic py-1">
        No files or folders yet.
      </div>
    );
  }

  return (
    <div
      className={`quote-files-tree ${
        dropTargetUid === "root" ? "quote-files-tree--drop-root" : ""
      }`}
      onDragOver={(e) => {
        if (!dragNodeRef.current) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        clearExpandTimer();
        setDropTargetUid("root");
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setDropTargetUid(null);
          clearExpandTimer();
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        performDrop(null);
      }}
    >
      {treeItems.map((node) => renderNode(node, 0))}
    </div>
  );
};

export default QuoteFilesTree;
